import { BarSeriesOption, LineSeriesOption, SeriesOption } from 'echarts';
import {
  AnalysisEnergyGroup,
  AnalysisSensor,
  AnalysisTemplate,
  CreateTemplatePayload,
  CustomerSite,
  DailyEnergyApiResponse,
  EnergyItem,
  EnergySummaryResponse,
  GroupAnalysisGroupApi,
  GroupAnalysisSensorApi,
  GroupAnalysisTemplateApi,
  MdbPowerChartData,
  MdbPowerResponse,
  UpdateTemplatePayload,
} from './group-analysis.interface';
import { ResidenceProfile } from 'projects/lib/feature/setting/src/lib/types/setting.model';

export function mapTemplateApiToModel(
  item: GroupAnalysisTemplateApi,
): AnalysisTemplate {
  const groups = parseJsonArray<GroupAnalysisGroupApi>(item.groups).map(
    mapGroupApiToModel,
  );

  return {
    id: item.id,
    name: item.name,
    order: item.order,
    description: item.description ?? '',

    groups,
    groupCount: groups.length,
    meterCount: groups.reduce(
      (total, group) => total + group.sensors.length,
      0,
    ),

    createdAt: parseDate(item.created_at),
    updatedAt: parseDate(item.updated_at),
  };
}

export function mapEnergySummary(
  response: EnergySummaryResponse,
): EnergyItem[] {
  return response.groups_summary
    .map((group) => ({
      id: group.group_id,
      name: group.group_name,
      energy: group.total_usage,
      proportion: group.proportion_percent,
      hexColor: group.hex_color || '#999999',
    }))
    .sort((a, b) => Number(a.energy) - Number(b.energy));
}

export function mapDailyEnergy(data: DailyEnergyApiResponse): {
  categories: string[];
  series: BarSeriesOption[];
} {
  return {
    categories: data.bar_graph.labels.map(String),
    series: [
      {
        name: 'This Month',
        type: 'bar',
        data: data.bar_graph.datasets[0].this,
        itemStyle: {
          color: '#1554A5',
        },
      },
      {
        name: 'Last Month',
        type: 'bar',
        data: data.bar_graph.datasets[0].last,
        itemStyle: {
          color: '#A9D2F5',
        },
      },
    ],
  };
}

export function mapCreateTemplatePayload(
  template: AnalysisTemplate,
): CreateTemplatePayload {
  return {
    name: template.name,
    description: template.description,
    groups: template.groups.map((group) => ({
      name: group.name,
      hex_color: group.hexColor,
      sensor_ids: group.sensors.map((sensor) => sensor.id),
    })),
  };
}

export function mapUpdateTemplatePayload(
  template: AnalysisTemplate,
): UpdateTemplatePayload {
  return {
    name: template.name,
    description: template.description,
    groups: template.groups.map((group) => ({
      id: group.id != null ? Number(group.id) : undefined,
      name: group.name,
      hex_color: group.hexColor,
      order: group.order,
      sensor_ids: group.sensors.map((sensor) => sensor.id),
    })),
  };
}

function mapGroupApiToModel(group: GroupAnalysisGroupApi): AnalysisEnergyGroup {
  const sensors = parseJsonArray<GroupAnalysisSensorApi>(group.sensors).map(
    mapSensorApiToModel,
  );

  return {
    id: group.id,
    name: group.name,
    hexColor: group.hex_color,
    order: group.order,
    sensors,
  };
}

function mapSensorApiToModel(sensor: GroupAnalysisSensorApi): AnalysisSensor {
  return {
    id: sensor.id,
    name: sensor.name,
  };
}

function parseJsonArray<T>(value: T[] | string | null | undefined): T[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== 'string') {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(value);

    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function mapCustomerSite(profile: ResidenceProfile): CustomerSite {
  return {
    name: profile.nameEn,
    address: [
      profile.addressEn,
      profile.subDistrictEn,
      profile.districtEn,
      profile.provinceEn,
      profile.postalCode,
    ]
      .filter(Boolean)
      .join(', '),
    imageUrl: 'assets/branding/banner-bg.png',
  };
}

export function mapMdbPower(data: MdbPowerResponse): MdbPowerChartData {
  const entries = Object.entries(data);

  if (!entries.length) {
    return {
      categories: [],
      series: [],
    };
  }

  const categories = Array.from(
    new Set(
      entries.flatMap(([, items]) =>
        items.map((item) => formatTime(item.hour, item.minute)),
      ),
    ),
  ).sort((a, b) => timeToMinutes(a) - timeToMinutes(b));

  const series: LineSeriesOption[] = entries.map(([mdbId, items]) => {
    const valueMap = new Map(
      items.map((item) => [formatTime(item.hour, item.minute), item.kw]),
    );

    return {
      name: formatMdbName(mdbId),
      type: 'line',
      data: categories.map((category) => valueMap.get(category) ?? null),
      showSymbol: false,
      connectNulls: false,
    };
  });

  return {
    categories,
    series,
  };
}

function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function timeToMinutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number);

  return hour * 60 + minute;
}

function formatMdbName(mdbId: string): string {
  return mdbId.replace(/^A-EMDB/, 'MDB');
}
